import { useState } from 'react';
import useCioClient from './useCioClient';
import useDownShift from './useDownShift';
import usePrevious from './usePrevious';
import { getItemPosition } from '../utils';
import useConsoleErrors from './useConsoleErrors';
import useSections from './useSections';
import useItems from './useItems';
export const defaultSections = [
    {
        identifier: 'Search Suggestions',
        type: 'autocomplete',
    },
    {
        identifier: 'Products',
        type: 'autocomplete',
    },
];
const useCioAutocomplete = (options) => {
    const { onSubmit, onChange, openOnFocus, apiKey, cioJsClient, placeholder = 'What can we help you find today?', sections = defaultSections, zeroStateSections, autocompleteClassName = 'cio-autocomplete', advancedParameters, } = options;
    const [query, setQuery] = useState('');
    const previousQuery = usePrevious(query);
    const cioClient = useCioClient({ apiKey, cioJsClient });
    // Get autocomplete sections (autocomplete + recommendations + custom)
    const { activeSections, activeSectionsWithData, zeroStateActiveSections } = useSections(query, cioClient, sections, zeroStateSections, advancedParameters);
    // Get dropdown items array from active sections (autocomplete + recommendations + custom)
    const items = useItems(activeSectionsWithData);
    const downshift = useDownShift({ setQuery, items, onSubmit, cioClient, previousQuery });
    const { isOpen, getMenuProps, getLabelProps, openMenu, closeMenu, highlightedIndex } = downshift;
    // Log console errors
    useConsoleErrors(sections, activeSections);
    return {
        query,
        sections: activeSectionsWithData,
        isOpen: isOpen && items?.length > 0,
        getMenuProps: () => ({
            ...getMenuProps(),
            className: 'cio-results',
            'data-testid': 'cio-results',
        }),
        getLabelProps,
        openMenu,
        closeMenu,
        getItemProps: (item) => {
            const { index, sectionId } = getItemPosition({ item, items });
            const sectionItemTestId = `cio-item-${sectionId?.replace(' ', '')}`;
            return {
                ...downshift.getItemProps({ item, index }),
                className: `cio-item ${sectionItemTestId}`,
                'data-testid': sectionItemTestId,
            };
        },
        getInputProps: () => ({
            ...downshift.getInputProps({
                onChange: (e) => {
                    setQuery(e.target.value);
                    if (onChange) {
                        onChange(e.target.value);
                    }
                },
            }),
            value: query,
            onFocus: () => {
                if (options.onFocus) {
                    options.onFocus();
                }
                if (zeroStateActiveSections && openOnFocus !== false) {
                    downshift.openMenu();
                }
                if (query?.length) {
                    downshift.openMenu();
                }
                try {
                    cioClient?.tracker?.trackInputFocus();
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                }
            },
            className: 'cio-input',
            'data-testid': 'cio-input',
            placeholder,
            onKeyDownCapture: ({ code }) => {
                const isEnter = code === 'Enter';
                const isUserInput = highlightedIndex < 0;
                if (isOpen && isEnter && isUserInput && query?.length) {
                    if (onSubmit) {
                        onSubmit({ query });
                    }
                    try {
                        cioClient?.tracker.trackSearchSubmit(query, { originalQuery: query });
                    }
                    catch (error) {
                        // eslint-disable-next-line no-console
                        console.log(error);
                    }
                }
            },
        }),
        getFormProps: () => ({
            onSubmit: (event) => {
                event.preventDefault();
                if (onSubmit) {
                    onSubmit({ query });
                }
                try {
                    cioClient?.tracker.trackSearchSubmit(query, { originalQuery: query });
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                }
                return { query };
            },
            className: 'cio-form',
            'data-testid': 'cio-form',
        }),
        setQuery,
        cioClient,
        autocompleteClassName,
        selectedItem: items[highlightedIndex],
        advancedParameters,
    };
};
export default useCioAutocomplete;
